import React, { useEffect, useRef, useState } from 'react';
import { cta } from '../../content';
import { Arrow, Button, SmartLink, Swipe, prefersReducedMotion, usePageMeta } from '../../ui';
import { SharedNav } from '../../ui/chrome';
import { SECTORS, sectorByKey, sectorIndex, type Sector } from './sectors';
import { CHAPTERS } from './chapters';
import { Img, Plate } from './parts';
import { Story } from './Story';
import { AFTER_CSS, Belief, Book, Compliance, Expert, Proof, RevenueOps, TrustBand } from './After';
import { GapSection } from '../../story';
import { allUnits, rangeUnits } from '../../heroUnits';
import { storyStore, useStory } from './store';
import { CSS } from './styles';
import { OwnerPair } from '../../phone';
import { BackOfficeSection } from '../../insights';

// Variant C · "The life of one unit": a scroll-told story of one fictional machine.

const CYCLE_MS = 5600;

// Warm the cache so a cut never lands on a blank frame.
function usePreload() {
  useEffect(() => {
    SECTORS.forEach((x) => [x.scene, x.sceneFallback, x.product].forEach((u) => (new Image().src = u)));
  }, []);
}

// Only cycle through units whose real scene photo exists: a stand-in of the wrong product is worse than none.
// Units join the rotation by themselves as their photos are published.
const ALWAYS = new Set([SECTORS[0].key as string]);
function useReadyUnits() {
  const [ready, setReady] = useState<Set<string>>(() => new Set(ALWAYS));
  useEffect(() => {
    SECTORS.forEach((x) => {
      const img = new Image();
      img.onload = () => setReady((r) => (r.has(x.key) ? r : new Set(r).add(x.key)));
      img.src = x.scene;
    });
  }, []);
  return ready;
}

function Hero({ s }: { s: Sector }) {
  const ready = useReadyUnits();
  const { inStory } = useStory();
  const shown = sectorIndex(s.key);
  const [target, setTarget] = useState(shown);
  const [auto, setAuto] = useState(true);
  const [hover, setHover] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [tabOn, setTabOn] = useState(true);
  const [wipe, setWipe] = useState(0);
  const heroRef = useRef<HTMLElement>(null);
  usePreload();

  useEffect(() => {
    if (prefersReducedMotion()) setAuto(false);
    const vis = () => setTabOn(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', vis);
    const el = heroRef.current;
    let io: IntersectionObserver | undefined;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(([e]) => setOnScreen(e.intersectionRatio >= 0.3), { threshold: [0, 0.3, 0.6, 1] });
      io.observe(el);
    }
    return () => {
      document.removeEventListener('visibilitychange', vis);
      io?.disconnect();
    };
  }, []);

  // Cut, don't fade: the gradient sweeps across the stage and the next unit is underneath.
  useEffect(() => {
    if (target === shown) return;
    if (prefersReducedMotion()) {
      storyStore.set({ sector: SECTORS[target].key });
      return;
    }
    setWipe((w) => w + 1);
    const t = window.setTimeout(() => storyStore.set({ sector: SECTORS[target].key }), 210);
    return () => window.clearTimeout(t);
  }, [target, shown]);

  const paused = !auto || hover || !onScreen || !tabOn || inStory;
  const pick = (k: number) => {
    setAuto(false);
    setTarget(k);
  };
  const advance = () =>
    setTarget((t) => {
      for (let n = 1; n <= SECTORS.length; n++) {
        const k2 = (t + n) % SECTORS.length;
        if (ready.has(SECTORS[k2].key)) return k2;
      }
      return t;
    });
  // Touch has no hover: hold the ticker still while a finger is on it, and for a moment after.
  const [held, setHeld] = useState(false);
  const holdT = useRef<number>();
  const hold = () => {
    window.clearTimeout(holdT.current);
    setHeld(true);
  };
  const release = () => {
    window.clearTimeout(holdT.current);
    holdT.current = window.setTimeout(() => setHeld(false), 2500);
  };
  useEffect(() => () => window.clearTimeout(holdT.current), []);

  return (
    <section className="vc-hero" ref={heroRef}>
      <div className="wrap vc-hero-grid">
        <div className="vc-hero-copy">
          <h1 className="vc-h1">
            From the factory to <Swipe draw>every owner</Swipe>.
          </h1>
          <p className="vc-sub">Know who owns every unit, what it needs and which parts fit.</p>
          <div className="vc-hero-ctas">
            <Button href={cta.primaryHref}>
              {cta.primary} <Arrow />
            </Button>
            <SmartLink href={cta.secondaryHref} className="vc-quiet">
              or take the tour
            </SmartLink>
          </div>
        </div>
        <div
          className="vc-hero-vis"
          onPointerEnter={() => setHover(true)}
          onPointerLeave={() => setHover(false)}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
          onClick={() => setAuto(false)}
        >
          <Img key={s.key} src={s.scene} fallback={s.sceneFallback} alt={s.sceneAlt} width={1600} height={1067} className="vc-hero-img" style={{ objectPosition: s.scenePos }} />
          <div className="vc-scrim" aria-hidden="true" />
          {/* Ticker: the chip set twice, translated by half its width. The copy is aria-hidden and out of the tab order. */}
          <div
            className={['vc-chips', held ? 'is-held' : ''].filter(Boolean).join(' ')}
            role="group"
            aria-label="Choose the unit to follow"
            onPointerDown={hold}
            onPointerUp={release}
            onPointerCancel={release}
          >
            <div className="vc-chips-track">
              {[false, true].map((dup) => (
                <div key={dup ? 'b' : 'a'} className="vc-chips-set" aria-hidden={dup || undefined}>
                  {SECTORS.map((x, k) => !ready.has(x.key) && k !== target ? null : (
                    <button key={x.key} type="button" className="vc-chipbtn" aria-pressed={k === target} tabIndex={dup ? -1 : undefined} onClick={() => pick(k)}>
                      <span className="vc-chip-thumb" aria-hidden="true">
                        <Img src={x.product} fallback={x.sceneFallback} alt="" width={56} height={56} />
                      </span>
                      <span className="vc-chip-l">{x.chip}</span>
                      <span className="vc-chip-s">{x.chipShort}</span>
                      {k === target && auto && (
                        <i
                          key={`f-${target}`}
                          className="vc-chipfill"
                          aria-hidden="true"
                          style={{ animationDuration: `${CYCLE_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                          onAnimationEnd={dup ? undefined : advance}
                        />
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="vc-plate-wrap" role="img" aria-label={`Rating plate of a fictional ${s.kind.toLowerCase()}, ${s.fullModel}, serial ${s.serial}, with ${s.maker}'s code engraved beside the serial`}>
            <Plate s={s} key={s.key} />
          </div>
          {wipe > 0 && <span key={wipe} className="vc-wipe vc-wipe-hero" aria-hidden="true" />}
        </div>
      </div>
      <div className="wrap">
        <div className="vc-opens" style={{ '--vc-acc': s.accent } as React.CSSProperties}>
          <span className="vc-opens-link" aria-hidden="true" />
          <div className="vc-opens-copy">
            <div className="vc-opens-k">
              <span>Scan the code</span>
              <svg width="22" height="8" viewBox="0 0 22 8" aria-hidden="true">
                <path d="M0 4h20M17 1l3 3-3 3" stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>this opens</span>
            </div>
            <p className="vc-opens-t">
              An experience for the owner. A passport for the rules.
            </p>
            <p className="vc-opens-s">
              The owner gets the {s.fullModel}&rsquo;s own page in {s.maker}&rsquo;s brand. Repairers, recyclers and authorities get its passport.
            </p>
            <ul className="vc-opens-tags">
              <li>
                <SmartLink href="/platform/digital-product-passport">ESPR ready</SmartLink>
              </li>
              <li>
                <SmartLink href="/blog/manufacturer-guide-gs1-digital-link/">GS1 Digital Link</SmartLink>
              </li>
            </ul>
          </div>
          <div className="vc-opens-stage">
            <OwnerPair s={s} key={s.key} back="passport" />
          </div>
        </div>
        <ul className="vc-proof">
          <li>In your brand</li>
          <li>No app for owners</li>
          <li>Works with your ERP</li>
          <li>GDPR</li>
          <li>Live in weeks</li>
        </ul>
      </div>
    </section>
  );
}

// A quiet prompt where the story ends: one line and one button, not a block.
function StoryNext() {
  return (
    <div className="wrap">
      <div className="vc-next">
        <p>That was one unit. Now picture yours.</p>
        <Button href={cta.primaryHref} small>
          {cta.primary} <Arrow />
        </Button>
      </div>
    </div>
  );
}

export function Home() {
  usePageMeta(
    'BrandedMark | The ownership layer for physical products',
    'Follow one product from the factory to every owner. One code and one living record for every unit you make: handover, warranty, parts, claims, resale and passport, connected to your ERP.'
  );
  const { sector } = useStory();
  const s = sectorByKey(sector);
  const unit = allUnits.find((h) => h.key === sector) ?? rangeUnits[0];
  return (
    <div className="vc">
      <style>{CSS + AFTER_CSS}</style>
      <Hero s={s} />
      <Belief />
      <GapSection tone="dark" unit={unit} compact />
      <Story s={s} />
      <BackOfficeSection unit={unit} tone="white" />
      <StoryNext />
      <Expert unit={unit} />
      <RevenueOps unit={unit} />
      <Compliance />
      <TrustBand />
      <Proof />
      <Book />
    </div>
  );
}

// The shared nav with its menus. While the story is on screen, it also shows which unit and chapter
// you are on, and a progress line along its bottom edge.
export function Nav() {
  const st = useStory();
  const s = sectorByKey(st.sector);
  return (
    <SharedNav
      story={{
        active: st.inStory,
        serial: s.serial,
        chapter: `${String(st.active + 1).padStart(2, '0')} · ${CHAPTERS[st.active]?.stage ?? ''}`,
        progress: st.progress,
      }}
    />
  );
}
